import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  TrendingDown,
  DollarSign,
  Percent,
  Calculator,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Archive,
} from 'lucide-react';
import { barrattAPI, type SpendLeakageReport, type SpendLeakageItem } from '../lib/api';
import { format } from 'date-fns';
import { useFormatters } from '../contexts/PreferencesContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-500',
};

const categoryOptions = [
  { value: 'off_contract', label: 'Off-Contract Purchase' },
  { value: 'maverick', label: 'Maverick Spending' },
  { value: 'emergency', label: 'Emergency Purchase' },
  { value: 'non_preferred', label: 'Non-Preferred Supplier' },
  { value: 'price_variance', label: 'Price Variance' },
  { value: 'other', label: 'Other' },
];

const SpendLeakageDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<SpendLeakageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [newItem, setNewItem] = useState({
    po_number: '',
    description: '',
    category: '',
    spend_amount: '',
    contracted_price: '',
    transaction_date: '',
    notes: '',
  });

  const { formatCurrency: contextFormatCurrency } = useFormatters();

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return contextFormatCurrency(numAmount || 0);
  };

  const formatPercentage = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return (numValue || 0).toFixed(1);
  };

  const loadReport = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await barrattAPI.getSpendLeakageReport(parseInt(id, 10));
      setReport(response.data);
    } catch (err) {
      console.error('Failed to load spend leakage report:', err);
      setError('Failed to load report. It may have been deleted or you may not have permission.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleAddItem = async () => {
    if (!report) return;

    try {
      await barrattAPI.addSpendLeakageItem(report.id, {
        po_number: newItem.po_number || undefined,
        description: newItem.description,
        category: newItem.category,
        spend_amount: parseFloat(newItem.spend_amount),
        contracted_price: newItem.contracted_price ? parseFloat(newItem.contracted_price) : undefined,
        transaction_date: newItem.transaction_date,
        notes: newItem.notes || undefined,
      });
      setAddItemDialogOpen(false);
      setNewItem({
        po_number: '',
        description: '',
        category: '',
        spend_amount: '',
        contracted_price: '',
        transaction_date: '',
        notes: '',
      });
      toast.success('Leakage item added successfully');
      loadReport();
    } catch (err) {
      console.error('Failed to add item:', err);
      toast.error('Failed to add leakage item');
    }
  };

  const handleRecalculate = async () => {
    if (!report) return;

    setRecalculating(true);
    try {
      await barrattAPI.recalculateSpendLeakageReport(report.id);
      toast.success('Report recalculated successfully');
      loadReport();
    } catch (err) {
      console.error('Failed to recalculate:', err);
      toast.error('Failed to recalculate report');
    } finally {
      setRecalculating(false);
    }
  };

  const openPublishDialog = () => {
    setPublishDialogOpen(true);
  };

  const handlePublish = async () => {
    if (!report) return;

    setPublishing(true);
    try {
      await barrattAPI.publishSpendLeakageReport(report.id);
      toast.success('Report published successfully');
      setPublishDialogOpen(false);
      loadReport();
    } catch (err) {
      console.error('Failed to publish:', err);
      toast.error('Failed to publish report');
    } finally {
      setPublishing(false);
    }
  };

  const openArchiveDialog = () => {
    setArchiveDialogOpen(true);
  };

  const handleArchive = async () => {
    if (!report) return;

    setArchiving(true);
    try {
      await barrattAPI.archiveSpendLeakageReport(report.id);
      toast.success('Report archived successfully');
      setArchiveDialogOpen(false);
      navigate('/spend-leakage');
    } catch (err) {
      console.error('Failed to archive:', err);
      toast.error('Failed to archive report');
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Report Not Found</h3>
              <p className="text-gray-500 mb-4">
                {error || 'The requested spend leakage report could not be found.'}
              </p>
              <Button onClick={() => navigate('/spend-leakage')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const items = report.items || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/spend-leakage')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{report.title}</h1>
              <Badge className={statusColors[report.status]}>
                {report.status_display}
              </Badge>
            </div>
            <p className="text-gray-500">
              Report #{report.report_number} | {format(new Date(report.period_start), 'dd MMM')} -{' '}
              {format(new Date(report.period_end), 'dd MMM yyyy')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadReport}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {report.status === 'draft' && (
            <>
              <Button variant="outline" onClick={handleRecalculate} disabled={recalculating}>
                {recalculating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Recalculate
              </Button>
              <Button onClick={openPublishDialog}>Publish Report</Button>
            </>
          )}
          {report.status === 'published' && (
            <Button variant="outline" onClick={openArchiveDialog}>
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Spend</p>
                <p className="text-2xl font-bold">{formatCurrency(report.total_spend)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Contracted Spend</p>
                <p className="text-2xl font-bold">{formatCurrency(report.contracted_spend)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Leakage Amount</p>
                <p className="text-2xl font-bold text-red-700">{formatCurrency(report.leakage_spend)}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Leakage %</p>
                <p className={`text-2xl font-bold ${parseFloat(String(report.leakage_percentage)) > 10 ? 'text-red-600' : ''}`}>
                  {formatPercentage(report.leakage_percentage)}%
                </p>
              </div>
              <Percent className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Description */}
      {report.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">{report.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Leakage Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Leakage Items</CardTitle>
            <CardDescription>{items.length} items identified</CardDescription>
          </div>
          {report.status === 'draft' && (
            <Button onClick={() => setAddItemDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No leakage items identified in this report
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PO Number</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item: SpendLeakageItem) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.po_number || '-'}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.category_display}</Badge>
                    </TableCell>
                    <TableCell>{item.supplier_display || item.supplier_name || '-'}</TableCell>
                    <TableCell>{format(new Date(item.transaction_date), 'dd MMM yyyy')}</TableCell>
                    <TableCell className="text-right">{formatCurrency(item.spend_amount)}</TableCell>
                    <TableCell className="text-right text-red-600">
                      {formatCurrency(item.variance_amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Report Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Report Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Created By</p>
              <p className="font-medium">{report.created_by_name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-gray-500">Created At</p>
              <p className="font-medium">{format(new Date(report.created_at), 'dd MMM yyyy HH:mm')}</p>
            </div>
            <div>
              <p className="text-gray-500">Last Updated</p>
              <p className="font-medium">{format(new Date(report.updated_at), 'dd MMM yyyy HH:mm')}</p>
            </div>
            {report.published_at && (
              <div>
                <p className="text-gray-500">Published At</p>
                <p className="font-medium">{format(new Date(report.published_at), 'dd MMM yyyy HH:mm')}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Item Dialog */}
      <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Leakage Item</DialogTitle>
            <DialogDescription>
              Add a new spend leakage item to this report
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>PO Number</Label>
              <Input
                value={newItem.po_number}
                onChange={(e) => setNewItem({ ...newItem, po_number: e.target.value })}
                placeholder="e.g., PO-12345"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Describe the spending"
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Select
                value={newItem.category}
                onValueChange={(value) => setNewItem({ ...newItem, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Spend Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.spend_amount}
                  onChange={(e) => setNewItem({ ...newItem, spend_amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Contracted Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.contracted_price}
                  onChange={(e) => setNewItem({ ...newItem, contracted_price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label>Transaction Date *</Label>
              <Input
                type="date"
                value={newItem.transaction_date}
                onChange={(e) => setNewItem({ ...newItem, transaction_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newItem.notes}
                onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddItem}
              disabled={!newItem.description || !newItem.category || !newItem.spend_amount || !newItem.transaction_date}
            >
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to publish this report? Once published, the report cannot be reverted to draft status.
            </DialogDescription>
          </DialogHeader>
          {report && (
            <div className="py-4 space-y-2">
              <p className="font-medium">{report.title}</p>
              <p className="text-sm text-gray-500">Report #{report.report_number}</p>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div>
                  <p className="text-gray-500">Total Spend</p>
                  <p className="font-medium">{formatCurrency(report.total_spend)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Leakage Amount</p>
                  <p className="font-medium text-red-600">{formatCurrency(report.leakage_spend)}</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">This action is irreversible</p>
                    <p>Publishing will finalize the report and make it available to all stakeholders.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPublishDialogOpen(false)}
              disabled={publishing}
            >
              Cancel
            </Button>
            <Button onClick={handlePublish} disabled={publishing}>
              {publishing ? 'Publishing...' : 'Confirm Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive this report? Archived reports are hidden from the active view but remain accessible via the archive filter.
            </DialogDescription>
          </DialogHeader>
          {report && (
            <div className="py-4 space-y-2">
              <p className="font-medium">{report.title}</p>
              <p className="text-sm text-gray-500">Report #{report.report_number}</p>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div>
                  <p className="text-gray-500">Total Spend</p>
                  <p className="font-medium">{formatCurrency(report.total_spend)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Leakage Amount</p>
                  <p className="font-medium text-red-600">{formatCurrency(report.leakage_spend)}</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-start gap-2">
                  <Archive className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Report will be archived</p>
                    <p>The report will be removed from the active view but can still be accessed using the archive filter.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveDialogOpen(false)}
              disabled={archiving}
            >
              Cancel
            </Button>
            <Button onClick={handleArchive} disabled={archiving}>
              {archiving ? 'Archiving...' : 'Confirm Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpendLeakageDetail;
